import { Router } from "express";
import { db } from "@workspace/db";
import { companiesTable, usersTable, tendersTable, subscriptionsTable, plansTable } from "@workspace/db";
import { eq, count } from "drizzle-orm";
import { requireAuth, requireAdmin, type AuthRequest } from "../lib/auth";

const router = Router();

router.get("/companies/me", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(404).json({ error: "No company profile" });
    const company = await db
      .select()
      .from(companiesTable)
      .where(eq(companiesTable.id, req.companyId))
      .limit(1);
    if (!company[0]) return res.status(404).json({ error: "Company not found" });
    res.json(company[0]);
  } catch (err) {
    next(err);
  }
});

router.put("/companies/me", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const {
      name, sector, targetRegions, experience, certifications,
      minProjectValue, maxProjectValue, preferredProjectTypes, bio,
      minBidScore,
    } = req.body;

    const toNumericOrNull = (v: any) =>
      v === "" || v == null ? null : String(v);

    if (req.companyId) {
      const updated = await db
        .update(companiesTable)
        .set({
          name, sector, targetRegions, experience, certifications,
          minProjectValue: toNumericOrNull(minProjectValue),
          maxProjectValue: toNumericOrNull(maxProjectValue),
          preferredProjectTypes, bio,
          ...(minBidScore != null ? { minBidScore: Number(minBidScore) } : {}),
          updatedAt: new Date(),
        })
        .where(eq(companiesTable.id, req.companyId))
        .returning();
      return res.json(updated[0]);
    }

    // Create new company
    const created = await db
      .insert(companiesTable)
      .values({
        name, sector, targetRegions, experience, certifications,
        minProjectValue: toNumericOrNull(minProjectValue),
        maxProjectValue: toNumericOrNull(maxProjectValue),
        preferredProjectTypes, bio,
        ...(minBidScore != null ? { minBidScore: Number(minBidScore) } : {}),
      })
      .returning();

    // Link user to company
    await db
      .update(usersTable)
      .set({ companyId: created[0].id, updatedAt: new Date() })
      .where(eq(usersTable.id, req.dbUser!.id));

    res.json(created[0]);
  } catch (err) {
    next(err);
  }
});

// Admin: list all companies with usage
router.get("/admin/companies", requireAdmin, async (req: AuthRequest, res, next) => {
  try {
    const companies = await db
      .select({
        id: companiesTable.id,
        name: companiesTable.name,
        sector: companiesTable.sector,
        analysisUsedThisMonth: companiesTable.analysisUsedThisMonth,
        createdAt: companiesTable.createdAt,
        planName: plansTable.name,
      })
      .from(companiesTable)
      .leftJoin(subscriptionsTable, eq(subscriptionsTable.companyId, companiesTable.id))
      .leftJoin(plansTable, eq(plansTable.id, subscriptionsTable.planId));

    const withTenders = await Promise.all(
      companies.map(async (c) => {
        const [result] = await db
          .select({ count: count() })
          .from(tendersTable)
          .where(eq(tendersTable.companyId, c.id));
        return { ...c, totalTenders: Number(result?.count ?? 0) };
      }),
    );

    res.json(withTenders);
  } catch (err) {
    next(err);
  }
});

export default router;
